import {useIntl} from "react-intl";
import './scss/DateRange.scss';
import {useEffect, useRef, useMemo, useState} from "react";

export const DateRange = ({ onFilterChange, dateRange }) => {
    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const dateRangeRef = useRef();
    const [manualInputs, setManualInputs] = useState({ start: '', end: '' });
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')

    // Parse the dateRange from URL parameter
    useEffect(()=>{
        if (dateRange?.[0]) {
            const [start, end] = dateRange[0].split('_');
            setManualInputs({ start, end })
            setStartDate(start)
            setEndDate(end)
        } else {
            setManualInputs({ start: '', end: '' })
            setStartDate('')
            setEndDate('')
        }
    },[dateRange])

    const formatQuarterRange = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        return `${start.toLocaleDateString(intl.locale, { month: 'short' })}–${end.toLocaleDateString(intl.locale, { month: 'short' })} ${start.getFullYear()}`;
    };

    const academicYears = useMemo(() => {
        const years = [];
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        const currentAcademicYear = currentMonth >= 7 ? currentYear : currentYear - 1;

        for (let i = 0; i < 5; i++) {
            const academicYear = currentAcademicYear - i;
            years.push({
                label: `${academicYear}–${academicYear + 1}`,
                startDate: `${academicYear}-07-01`,
                endDate: `${academicYear + 1}-06-30`,
                quarters: [
                    {
                        label: formatQuarterRange(`${academicYear}-07-01`, `${academicYear}-09-30`),
                        dateRange: `${academicYear}-07-01_${academicYear}-09-30`
                    },
                    {
                        label: formatQuarterRange(`${academicYear}-10-01`, `${academicYear}-12-31`),
                        dateRange: `${academicYear}-10-01_${academicYear}-12-31`
                    },
                    {
                        label: formatQuarterRange(`${academicYear + 1}-01-01`, `${academicYear + 1}-03-31`),
                        dateRange: `${academicYear + 1}-01-01_${academicYear + 1}-03-31`
                    },
                    {
                        label: formatQuarterRange(`${academicYear + 1}-04-01`, `${academicYear + 1}-06-30`),
                        dateRange: `${academicYear + 1}-04-01_${academicYear + 1}-06-30`
                    }
                ]
            });
        }
        return years;
    }, [intl.locale]);

    const handleClickOutside = (event) => {
        if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
            if (manualInputs.start && manualInputs.end) {
                handleDateSelect(`${manualInputs.start}_${manualInputs.end}`);
            }
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [manualInputs]);

    const handleDateSelect = (dateRange) => {
        if (!dateRange) {
            onFilterChange({ daterange: [] });
        } else {
            onFilterChange({ daterange: [dateRange] });
        }
        setIsOpen(false);
    };

    const handleAcademicYearSelect = (academicYear) => {
        handleDateSelect(`${academicYear.startDate}_${academicYear.endDate}`);
    };

    const handleManualInputChange = (start, end) => {
        const newInputs = {
            start: start ?? manualInputs.start,
            end: end ?? manualInputs.end
        };
        setManualInputs(newInputs);

        // Only set the date range when both dates are present
        if (newInputs.start && newInputs.end && new Date(newInputs.start) <= new Date(newInputs.end)) {
            onFilterChange({ daterange: [`${newInputs.start}_${newInputs.end}`] });
        }
    };

    const formatDisplayDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString(intl.locale, {
            month: 'short',
            year: 'numeric'
        });
    };

    const isQuarterInFuture = (dateRange) => {
        const [startDate] = dateRange.split('_');
        return new Date(startDate) > new Date();
    };

    const displayValue = startDate && endDate
        ? `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
        : intl.formatMessage({id: 'dateRange.selectDateRange', defaultMessage: 'Select date range'});

    return (
        <div className={`date-range ${startDate ? "active" : ""}`} ref={dateRangeRef}>
            <div className="select" onClick={() => setIsOpen(!isOpen)}>
                {displayValue}
            </div>

            {isOpen && (
                <div className="dropdown">
                    <div className="custom-range">
                        <input
                            type="date"
                            value={manualInputs.start}
                            onChange={(e) => handleManualInputChange(e.target.value, null)}
                            aria-label={intl.formatMessage({id: 'dateRange.startDate', defaultMessage: 'Start date'})}
                        />
                        <span>-</span>
                        <input
                            type="date"
                            value={manualInputs.end}
                            onChange={(e) => handleManualInputChange(null, e.target.value)}
                            aria-label={intl.formatMessage({id: 'dateRange.endDate', defaultMessage: 'End date'})}
                        />
                    </div>

                    <ul>
                        {academicYears.map((academicYear) => (
                            <li key={academicYear.label} className="academic-year">
                                <div
                                    className="year-header selectable"
                                    onClick={() => handleAcademicYearSelect(academicYear)}
                                >
                                    {academicYear.label}
                                </div>
                                {academicYear.quarters.map((quarter) => {
                                    const futureQuarter = isQuarterInFuture(quarter.dateRange);
                                    return (
                                        <div
                                            key={quarter.dateRange}
                                            className={`quarter ${futureQuarter ? 'disabled' : ''}`}
                                            onClick={() => !futureQuarter && handleDateSelect(quarter.dateRange)}
                                            title={futureQuarter ?
                                                intl.formatMessage({
                                                    id: 'dateRange.futureQuarter',
                                                    defaultMessage: 'Future quarters are not available'
                                                }) : undefined}
                                        >
                                            {quarter.label}
                                        </div>
                                    );
                                })}
                            </li>
                        ))}
                    </ul>

                    {dateRange?.length > 0 && (
                        <button onClick={() => {
                            handleDateSelect('');
                            setManualInputs({ start: '', end: '' });
                        }}>
                            {intl.formatMessage({id: 'clearSelection', defaultMessage: 'Clear Selection'})}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};